"use client"

import { useState, useCallback } from "react"
import Link from "next/link"
import { formatValue } from "@/lib/utils"
import { RefreshCw, Share2, Check } from "lucide-react"

interface Item {
  name: string
  slug: string | null
  value: number
}

interface GameTypeData {
  id: string
  name: string
  slug: string
  categoryName: string
  categorySlug: string
  valueLabel: string
  valueUnit: string | null
  valuePrefix: boolean
  items: Item[]
}

type TriviaCard = {
  question: string
  answer: string
  category: string
  type: "comparison" | "ranking" | "fact" | "gap"
}

function generateTrivia(types: GameTypeData[]): TriviaCard {
  const type = types[Math.floor(Math.random() * types.length)]
  const items = type.items
  const fmt = (v: number) => formatValue(v, type.valueUnit || undefined, type.valuePrefix)
  const templates = ["comparison", "ranking", "fact", "gap"] as const
  const template = templates[Math.floor(Math.random() * templates.length)]

  switch (template) {
    case "comparison": {
      const a = items[Math.floor(Math.random() * Math.min(items.length, 20))]
      let b = a
      while (b === a) b = items[Math.floor(Math.random() * items.length)]
      const higher = a.value >= b.value ? a : b
      const lower = a.value >= b.value ? b : a
      const ratio = higher.value / Math.max(lower.value, 1)
      return {
        question: `Who has more ${type.valueLabel.toLowerCase()}: ${a.name} or ${b.name}?`,
        answer: `${higher.name} (${fmt(higher.value)}) has ${ratio >= 2 ? `${ratio.toFixed(1)}x more than` : "more than"} ${lower.name} (${fmt(lower.value)}).`,
        category: `${type.categoryName} · ${type.name}`,
        type: "comparison",
      }
    }
    case "ranking": {
      const idx = Math.floor(Math.random() * Math.min(items.length, 10))
      const item = items[idx]
      return {
        question: `Where does ${item.name} rank in ${type.name.toLowerCase()} by ${type.valueLabel.toLowerCase()}?`,
        answer: `#${idx + 1} out of ${items.length} with ${fmt(item.value)}.`,
        category: `${type.categoryName} · ${type.name}`,
        type: "ranking",
      }
    }
    case "fact": {
      const top = items[0]
      const bottom = items[items.length - 1]
      const ratio = top.value / Math.max(bottom.value, 1)
      return {
        question: `How much bigger is #1 vs the last in ${type.name.toLowerCase()} ${type.valueLabel.toLowerCase()}?`,
        answer: `${top.name} (${fmt(top.value)}) is ${ratio >= 2 ? `${Math.round(ratio).toLocaleString()}x` : `${((ratio - 1) * 100).toFixed(0)}% more than`} ${bottom.name} (${fmt(bottom.value)}).`,
        category: `${type.categoryName} · ${type.name}`,
        type: "fact",
      }
    }
    case "gap": {
      let closestGap = Infinity
      let ci = 0
      for (let i = 0; i < items.length - 1; i++) {
        const gap = items[i].value - items[i + 1].value
        if (gap < closestGap && gap >= 0) { closestGap = gap; ci = i }
      }
      return {
        question: `Which two ${type.name.toLowerCase()} are the closest in ${type.valueLabel.toLowerCase()}?`,
        answer: `${items[ci].name} (${fmt(items[ci].value)}) and ${items[ci + 1].name} (${fmt(items[ci + 1].value)}) are only ${fmt(closestGap)} apart.`,
        category: `${type.categoryName} · ${type.name}`,
        type: "gap",
      }
    }
  }
}

const typeColors = {
  comparison: { bg: "bg-rose-500/10", border: "border-rose-500/20", text: "text-rose-400", label: "VS" },
  ranking: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", label: "RANK" },
  fact: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", label: "FACT" },
  gap: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400", label: "GAP" },
}

export function TriviaGenerator({ types }: { types: GameTypeData[] }) {
  const [cards, setCards] = useState<TriviaCard[]>(() =>
    Array.from({ length: 3 }, () => generateTrivia(types))
  )
  const [revealed, setRevealed] = useState<Set<number>>(new Set())
  const [copied, setCopied] = useState(false)

  const regenerate = useCallback(() => {
    setCards(Array.from({ length: 3 }, () => generateTrivia(types)))
    setRevealed(new Set())
  }, [types])

  const toggleReveal = (idx: number) => {
    setRevealed((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const shareAll = async () => {
    const text = cards
      .map((c, i) => `Q${i + 1}: ${c.question}\nA: ${c.answer}`)
      .join("\n\n")
    const shareText = `${text}\n\nGenerated at playhigherlower.com/generator`

    if (navigator.share) {
      try {
        await navigator.share({ title: "Trivia Questions", text: shareText })
        return
      } catch { /* fallback */ }
    }
    await navigator.clipboard.writeText(shareText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-4">
      {cards.map((card, i) => {
        const color = typeColors[card.type]
        const isRevealed = revealed.has(i)
        return (
          <div key={i} className={`rounded-2xl ${color.bg} border ${color.border} overflow-hidden`}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${color.text} ${color.bg}`}>{color.label}</span>
                <span className="text-xs text-zinc-500">{card.category}</span>
              </div>
              <p className="text-white font-medium text-lg mb-3">{card.question}</p>
              <button
                onClick={() => toggleReveal(i)}
                className={`w-full text-left p-3 rounded-xl transition-all ${
                  isRevealed
                    ? "bg-zinc-900/80 border border-zinc-700"
                    : "bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 cursor-pointer"
                }`}
              >
                {isRevealed ? (
                  <p className="text-sm text-zinc-300">{card.answer}</p>
                ) : (
                  <p className="text-sm text-zinc-500 italic">Tap to reveal answer</p>
                )}
              </button>
            </div>
          </div>
        )
      })}

      <div className="flex gap-3">
        <button
          onClick={regenerate}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white font-medium hover:bg-zinc-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Generate more
        </button>
        <button
          onClick={shareAll}
          className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl border font-medium transition-all ${
            copied
              ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400"
              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white"
          }`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          {copied ? "Copied" : "Share"}
        </button>
      </div>

      <div className="text-center">
        <Link href="/play" className="text-sm text-zinc-500 hover:text-white transition-colors">
          Think you know the answers? Play the game →
        </Link>
      </div>
    </div>
  )
}
